/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { GridItem } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { API_VERSION, ISDMProductTileData, TSDMSortOptionsType } from '@ldp/common-types';
import { useSDMViewportContext } from '@ldp/sdm-contexts';
import { Flex, Spacer } from '@ldp-web/component-library/ui/atoms/flex';
import { Box } from '@ldp-web/component-library/ui/atoms/box';
// @ts-ignore: federated module
import { ListingPageLayout } from '@ldp-web/component-library/ui/layouts/listing-page-layout';
// @ts-ignore: federated module
import { ViewResolverSection } from '../../view-resolver-section';
// @ts-ignore: federated module
import { useLayoutSectionV2, useViewResolverContext } from '@ldp/view-resolver-lite';
import { FilterChipComponent } from '../../filter-chip';
import { PaginationComponent } from '../../pagination';
import { TSDMFilterGroup, TSDMSearchAnalyticsContext, getSnowplowSearchPageContext } from '@ldp/sdm-analytics-utils';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { getFilterAndSortContext } from '../../../utils';
//analytics
import { useAnalyticsContext } from '@ldp/common-contexts';

const VIEW_RESOLVER_SECTION = {
  MAIN_CONTENT_COLLECTION: 'mainContentCollection',
  BANNER_SECTION: 'bannerSection',
  FILTER_SECTION: 'filtersSection',
  HEADLINE_SECTION: 'headlineSection',
  QUICK_FILTER_SECTION: 'quickFiltersSection',
  SORTS_SECTION: 'sortsSection',
};
const PRODUCT_GRID_COMPONENT = 'productGridComponent';

/** Listing page props */
export interface IListingPageComponentProps {
  /** children of the page */
  children?: React.ReactNode;
}

// Additional styles
const ListingPageHeadlineContainer = styled('div')`
  & > div.chakra-container {
    padding: 0;
    margin-left: 0;
    margin: 2rem 0 1.3rem;
  }
`;

const NoResultPageLayout = styled(ListingPageLayout)`
  grid-template-columns: 100% !important;
`;

/**
 * Renders Listing page structure
 */
export const ProductListingPage: FC<IListingPageComponentProps> = () => {
  const viewportContext = useSDMViewportContext();
  const isDesktopView = viewportContext.viewport === 'desktop';
  const {
    MAIN_CONTENT_COLLECTION,
    BANNER_SECTION,
    FILTER_SECTION,
    HEADLINE_SECTION,
    QUICK_FILTER_SECTION,
    SORTS_SECTION,
  } = VIEW_RESOLVER_SECTION;
  const apiVersion = API_VERSION.v2;
  const i18n = useSimpleTranslation();
  const {
    viewDefinition: {
      searchRequestId,
      searchTerm,
      searchTermSubmitted,
      typedSearchTerms,
      modelVersion,
      searchVariation,
    },
  } = useViewResolverContext();
  const { trackEvent } = useAnalyticsContext();

  // Get filtersGroups data from filtersSection to render filter chips
  const filtersSection = useLayoutSectionV2(FILTER_SECTION);
  const filterGroups =
    (filtersSection.find((component) => component.componentId === 'filtersComponent')?.data
      ?.filterGroups as TSDMFilterGroup[]) || [];

  // mainContentCollection can contain multiple product grids
  // We need the last product grid information for pagination
  const mainContentCollection = useLayoutSectionV2(MAIN_CONTENT_COLLECTION);
  const lastProductGridComponent = mainContentCollection.reduceRight((accumulator: any, component: any) => {
    if (!accumulator && component.componentId === PRODUCT_GRID_COMPONENT) {
      return component;
    }
    return accumulator;
  }, null);

  const pagination = lastProductGridComponent?.data?.pagination;
  const showPagination = pagination?.totalResults > pagination?.pageSize;

  //Analytics start

  const sortsSection = useLayoutSectionV2(SORTS_SECTION);
  const sortData =
    (sortsSection.find((component) => component.componentId === 'sortsComponent')?.data
      ?.sorts as TSDMSortOptionsType[]) || [];

  const filterSortContext = useMemo(() => getFilterAndSortContext(filterGroups, sortData), [filterGroups, sortData]);

  const productGridComponent = mainContentCollection?.filter((item) => item.componentId === PRODUCT_GRID_COMPONENT);

  const productData = useMemo(
    () =>
      productGridComponent?.flatMap(
        (productTilesItem) => productTilesItem.data.productTiles as ISDMProductTileData[]
      ) || [],
    [productGridComponent]
  );

  const trackProductSearch = useCallback(
    (analyticsContext: TSDMSearchAnalyticsContext) => {
      const context = getSnowplowSearchPageContext(analyticsContext);
      trackEvent(context.eventType, context);
    },
    [trackEvent]
  );

  const handleProductSearchAnalytics = () => {
    const pageSize = pagination?.pageSize || 48;
    const totalResults = pagination?.totalResults || 0;
    /** track event call for product_search */
    trackProductSearch({
      pagination: {
        currentPage: pagination?.pageNumber || 0,
        pageSize,
        totalResults,
      },
      products: productData,
      search: {
        searchRequestId: searchRequestId || '',
        searchTerms: searchTerm || '',
        searchTermSubmitted: searchTermSubmitted || '',
        typedSearchTerms: typedSearchTerms || '',
        totalSearchResults: totalResults || 0,
      },
      dsModel: [
        {
          type: 'modelVersion',
          value: modelVersion || '',
        },
        {
          type: 'searchVariation',
          value: searchVariation || '',
        },
      ],
      filters: filterSortContext.filters || [],
      sortEventValue: filterSortContext.sort || '',
    });
  };

  useEffect(() => {
    if (window?.location.pathname === '/search') {
      handleProductSearchAnalytics();
    }
  }, []);

  //Analytics end

  const ListingPageWrapper = mainContentCollection.length > 0 ? ListingPageLayout : NoResultPageLayout;

  return (
    <>
      <ViewResolverSection apiVersion={apiVersion} layoutSection={BANNER_SECTION} />
      <ListingPageWrapper>
        {isDesktopView ? (
          <GridItem>
            <ViewResolverSection apiVersion={apiVersion} layoutSection={FILTER_SECTION} />
          </GridItem>
        ) : null}
        <GridItem>
          <ListingPageHeadlineContainer>
            <ViewResolverSection apiVersion={apiVersion} layoutSection={HEADLINE_SECTION} />
          </ListingPageHeadlineContainer>
          {!isDesktopView ? (
            <Box display={{ base: 'flex', lg: 'none' }} style={{ marginBottom: '8px' }}>
              <ViewResolverSection apiVersion={apiVersion} layoutSection={FILTER_SECTION} />
            </Box>
          ) : null}
          <Flex>
            <Box>
              <ViewResolverSection apiVersion={apiVersion} layoutSection={QUICK_FILTER_SECTION} />
            </Box>
            <Spacer />
            {isDesktopView ? (
              <Box>
                <ViewResolverSection apiVersion={apiVersion} layoutSection={SORTS_SECTION} />
              </Box>
            ) : null}
          </Flex>
          <FilterChipComponent
            filterGroups={filterGroups}
            removeFilterLabel={i18n('removeFilters')}
            clearAllLabel={i18n('clearAll')}
          />
          <ViewResolverSection apiVersion={apiVersion} layoutSection={MAIN_CONTENT_COLLECTION} />
          {showPagination && (
            <PaginationComponent
              page={pagination?.pageNumber}
              pageSize={pagination?.pageSize}
              totalResults={pagination?.totalResults}
              showGapForPagination={!isDesktopView}
            />
          )}
        </GridItem>
      </ListingPageWrapper>
    </>
  );
};

export default ProductListingPage;
